import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import './AdminTokenManager.css';

const AdminTokenManager = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingToken, setEditingToken] = useState(null);

  // Form state for creating/editing tokens
  const [tokenForm, setTokenForm] = useState({
    name: '',
    symbol: '',
    contractAddress: '',
    description: '',
    category: '',
    isActive: true,
    riskLevel: 'medium',
    minInvestment: 0,
    maxInvestment: 1000,
    expectedReturn: 0,
    liquidityScore: 0
  });

  useEffect(() => {
    if (user && isAdmin()) {
      fetchTokens();
    }
  }, [user]);

  const isAdmin = () => {
    return user && (user.role === 'admin' || user.isPoolManager);
  };

  const fetchTokens = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      const response = await fetch('/api/admin/tokens', {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTokens(data.tokens || []);
      } else {
        throw new Error('Failed to fetch tokens');
      }
    } catch (error) {
      console.error('Error fetching tokens:', error);
      showToast('Failed to load tokens', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      const response = await fetch('/api/admin/tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify(tokenForm)
      });

      if (response.ok) {
        const data = await response.json();
        setTokens(prev => [...prev, data.token]);
        setShowCreateModal(false);
        resetForm();
        showToast('Token created successfully', 'success');
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create token');
      }
    } catch (error) {
      console.error('Error creating token:', error);
      showToast(error.message, 'error');
    }
  };

  const handleUpdateToken = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      const response = await fetch(`/api/admin/tokens/${editingToken.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify(tokenForm)
      });

      if (response.ok) {
        const data = await response.json();
        setTokens(prev => prev.map(token =>
          token.id === editingToken.id ? data.token : token
        ));
        setEditingToken(null);
        resetForm();
        showToast('Token updated successfully', 'success');
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update token');
      }
    } catch (error) {
      console.error('Error updating token:', error);
      showToast(error.message, 'error');
    }
  };

  const handleDeleteToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to delete this token?')) {
      return;
    }

    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      const response = await fetch(`/api/admin/tokens/${tokenId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`
        }
      });

      if (response.ok) {
        setTokens(prev => prev.filter(token => token.id !== tokenId));
        showToast('Token deleted successfully', 'success');
      } else {
        throw new Error('Failed to delete token');
      }
    } catch (error) {
      console.error('Error deleting token:', error);
      showToast(error.message, 'error');
    }
  };

  const handleToggleStatus = async (tokenId, currentStatus) => {
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      const response = await fetch(`/api/admin/tokens/${tokenId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (response.ok) {
        setTokens(prev => prev.map(token =>
          token.id === tokenId ? { ...token, isActive: !currentStatus } : token
        ));
        showToast(`Token ${!currentStatus ? 'activated' : 'deactivated'} successfully`, 'success');
      } else {
        throw new Error('Failed to update token status');
      }
    } catch (error) {
      console.error('Error updating token status:', error);
      showToast(error.message, 'error');
    }
  };

  const resetForm = () => {
    setTokenForm({
      name: '',
      symbol: '',
      contractAddress: '',
      description: '',
      category: '',
      isActive: true,
      riskLevel: 'medium',
      minInvestment: 0,
      maxInvestment: 1000,
      expectedReturn: 0,
      liquidityScore: 0
    });
  };

  const openEditModal = (token) => {
    setEditingToken(token);
    setTokenForm({
      name: token.name,
      symbol: token.symbol,
      contractAddress: token.contractAddress,
      description: token.description || '',
      category: token.category || '',
      isActive: token.isActive,
      riskLevel: token.riskLevel || 'medium',
      minInvestment: token.minInvestment || 0,
      maxInvestment: token.maxInvestment || 1000,
      expectedReturn: token.expectedReturn || 0,
      liquidityScore: token.liquidityScore || 0
    });
  };

  const handleFormChange = (field, value) => {
    setTokenForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isAdmin()) {
    return (
      <div className="admin-access-denied">
        <h2>Access Denied</h2>
        <p>You don't have permission to access the admin token manager.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading token manager...</p>
      </div>
    );
  }

  return (
    <div className="admin-token-manager">
      <div className="manager-header">
        <div className="header-content">
          <h1>Token Listings Manager</h1>
          <p>Manage token listings, categories, and investment parameters</p>
        </div>
        <button
          className="create-token-btn"
          onClick={() => setShowCreateModal(true)}
        >
          Add New Token
        </button>
      </div>

      {/* Token Statistics */}
      <div className="token-stats">
        <div className="stat-card">
          <div className="stat-value">{tokens.length}</div>
          <div className="stat-label">Total Tokens</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{tokens.filter(t => t.isActive).length}</div>
          <div className="stat-label">Active Tokens</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{tokens.filter(t => !t.isActive).length}</div>
          <div className="stat-label">Inactive Tokens</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {tokens.length > 0 ? Math.round(tokens.filter(t => t.isActive).length / tokens.length * 100) : 0}%
          </div>
          <div className="stat-label">Active Rate</div>
        </div>
      </div>

      {/* Tokens Table */}
      <div className="tokens-section">
        <h2>Managed Tokens</h2>
        {tokens.length === 0 ? (
          <div className="no-tokens">
            <p>No tokens listed yet. Add your first token to get started.</p>
          </div>
        ) : (
          <div className="tokens-table-container">
            <table className="tokens-table">
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Symbol</th>
                  <th>Category</th>
                  <th>Risk Level</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map(token => (
                  <tr key={token.id}>
                    <td>
                      <div className="token-info">
                        <div className="token-name">{token.name}</div>
                        <div className="token-address">{token.contractAddress}</div>
                      </div>
                    </td>
                    <td>{token.symbol}</td>
                    <td>{token.category || 'N/A'}</td>
                    <td>
                      <span className={`risk-badge risk-${token.riskLevel || 'medium'}`}>
                        {token.riskLevel || 'Medium'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${token.isActive ? 'active' : 'inactive'}`}>
                        {token.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn edit"
                          onClick={() => openEditModal(token)}
                          title="Edit Token"
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn toggle"
                          onClick={() => handleToggleStatus(token.id, token.isActive)}
                          title={token.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {token.isActive ? '🚫' : '✅'}
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => handleDeleteToken(token.id)}
                          title="Delete Token"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingToken) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingToken ? 'Edit Token' : 'Add New Token'}</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingToken(null);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <form className="token-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Token Name *</label>
                    <input
                      type="text"
                      value={tokenForm.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder="Enter token name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Symbol *</label>
                    <input
                      type="text"
                      value={tokenForm.symbol}
                      onChange={(e) => handleFormChange('symbol', e.target.value)}
                      placeholder="Enter token symbol"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Contract Address *</label>
                  <input
                    type="text"
                    value={tokenForm.contractAddress}
                    onChange={(e) => handleFormChange('contractAddress', e.target.value)}
                    placeholder="Enter Solana contract address"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Category</label>
                    <select
                      value={tokenForm.category}
                      onChange={(e) => handleFormChange('category', e.target.value)}
                    >
                      <option value="">Select category</option>
                      <option value="DeFi">DeFi</option>
                      <option value="Gaming">Gaming</option>
                      <option value="NFT">NFT</option>
                      <option value="Utility">Utility</option>
                      <option value="Meme">Meme</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Risk Level</label>
                    <select
                      value={tokenForm.riskLevel}
                      onChange={(e) => handleFormChange('riskLevel', e.target.value)}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Min Investment (SOL)</label>
                    <input
                      type="number"
                      value={tokenForm.minInvestment}
                      onChange={(e) => handleFormChange('minInvestment', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Max Investment (SOL)</label>
                    <input
                      type="number"
                      value={tokenForm.maxInvestment}
                      onChange={(e) => handleFormChange('maxInvestment', parseFloat(e.target.value) || 1000)}
                      min="0"
                      step="0.1"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Expected Return (%)</label>
                    <input
                      type="number"
                      value={tokenForm.expectedReturn}
                      onChange={(e) => handleFormChange('expectedReturn', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Liquidity Score</label>
                    <input
                      type="number"
                      value={tokenForm.liquidityScore}
                      onChange={(e) => handleFormChange('liquidityScore', parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={tokenForm.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    placeholder="Enter token description"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={tokenForm.isActive}
                      onChange={(e) => handleFormChange('isActive', e.target.checked)}
                    />
                    Active (visible to users)
                  </label>
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingToken(null);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                className="submit-btn"
                onClick={editingToken ? handleUpdateToken : handleCreateToken}
                disabled={!tokenForm.name || !tokenForm.symbol || !tokenForm.contractAddress}
              >
                {editingToken ? 'Update Token' : 'Create Token'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTokenManager;
